import { useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, MapPin, Award, Clock, Globe, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useLocale } from "@/i18n/LocaleContext";
import { openCalendly } from "@/lib/calendly";
import logo from "@/assets/logo-equalitas.webp";
import flagUK from "@/assets/flag-uk.png";
import flagSpain from "@/assets/flag-spain.png";
import flagFrance from "@/assets/flag-france.png";
import type { Locale } from "@/i18n/translations";

const LOCALE_FLAGS: Record<Locale, { src: string; label: string }> = {
  es: { src: flagSpain, label: "Español" },
  en: { src: flagUK, label: "English" },
  fr: { src: flagFrance, label: "Français" },
};

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuToggleRef = useRef<HTMLButtonElement>(null);
  const location = useLocation();
  const { locale, t, localePath, switchLocalePath } = useLocale();

  const navLinks = [
    { href: localePath("/"), label: t("header.nav.home") },
    { href: localePath("/sobre-nosotros"), label: t("header.nav.about") },
    { href: localePath("/espacio-informativo"), label: t("header.nav.blog") },
  ];

  const otherLocales: Locale[] = (["es", "en", "fr"] as Locale[]).filter((l) => l !== locale);

  const isActive = (href: string) => location.pathname === href;

  return (
    <header className="sticky top-0 z-50 w-full">
      {/* Top Trust Bar */}
      <div className="bg-primary text-primary-foreground py-2 min-h-[32px]">
        {/* Mobile: 48h urgency message (EN/FR) or experience (ES) */}
        <div className="sm:hidden container mx-auto px-4 flex items-center justify-center text-center text-[12px] font-medium">
          {locale === "es" ? (
            <>
              <Clock className="h-3.5 w-3.5 mr-1.5 flex-shrink-0" aria-hidden="true" />
              <span>{t("header.topbar.experience")}</span>
            </>
          ) : (
            <>
              <Zap className="h-3.5 w-3.5 mr-1.5 flex-shrink-0" aria-hidden="true" />
              <span>{t("header.topbar.express48")}</span>
            </>
          )}
        </div>
        {/* Desktop: experience + ICAM + 48h (EN/FR) + address */}
        <div className="hidden sm:flex container mx-auto px-4 flex-wrap items-center justify-between gap-2 text-sm">
          <div className="flex items-center gap-6 flex-wrap justify-center">
            <span className="hidden sm:flex items-center gap-1.5">
              <Clock className="h-4 w-4" />
              {t("header.topbar.experience")}
            </span>
            <span className="hidden md:flex items-center gap-1.5">
              <Award className="h-4 w-4" />
              {t("header.topbar.icam")}
            </span>
            {locale !== "es" && (
              <span className="hidden lg:flex items-center gap-1.5 font-semibold">
                <Zap className="h-4 w-4 text-accent" />
                {t("header.topbar.express48")}
              </span>
            )}
          </div>
          <span className="flex items-center gap-1.5">
            <MapPin className="h-4 w-4" />
            {t("header.topbar.address")}
          </span>
        </div>
      </div>

      {/* Main Header */}
      <div className="bg-background border-b border-border">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          {/* Logo */}
          <Link to={localePath("/")} className="flex-shrink-0">
            <img
              src={logo}
              alt="Equalitas Abogados Asociados - Especialistas en Extranjería"
              className="h-14 md:h-[68px] w-auto"
              width={200}
              height={68}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm font-medium transition-colors hover:text-primary ${
                  isActive(link.href)
                    ? "text-primary border-b-2 border-accent pb-1"
                    : "text-foreground"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* CTA + Language Switcher */}
          <div className="hidden md:flex items-center gap-3">
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                >
                  <img
                    src={LOCALE_FLAGS[locale].src}
                    alt={LOCALE_FLAGS[locale].label}
                    className="h-4 w-6 object-cover"
                    width={24}
                    height={16}
                  />
                  <Globe className="h-4 w-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-40 p-1">
                {otherLocales.map((l) => (
                  <Link
                    key={l}
                    to={switchLocalePath(l)}
                    onClick={() => localStorage.setItem("preferred_locale", l)}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted rounded-sm transition-colors"
                  >
                    <img
                      src={LOCALE_FLAGS[l].src}
                      alt={LOCALE_FLAGS[l].label}
                      className="h-4 w-6 object-cover"
                      width={24}
                      height={16}
                    />
                    {LOCALE_FLAGS[l].label}
                  </Link>
                ))}
              </PopoverContent>
            </Popover>
            <Button
              onClick={() => openCalendly(locale)}
              className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold cursor-pointer"
            >
              {t("header.cta")}
            </Button>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            ref={menuToggleRef}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 text-foreground"
            aria-label={isMenuOpen ? t("header.menu.close") : t("header.menu.open")}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-nav"
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div id="mobile-nav" ref={menuRef} role="dialog" aria-modal="true" aria-label={locale === "en" ? "Navigation menu" : "Menú de navegación"} className="lg:hidden border-t border-border bg-background">
            <nav className="container mx-auto px-4 py-4 flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setIsMenuOpen(false)}
                  className={`text-base font-medium py-2 transition-colors ${
                    isActive(link.href)
                      ? "text-primary"
                      : "text-foreground hover:text-primary"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="border-t border-border pt-3 mt-1 flex flex-col gap-2">
                {otherLocales.map((l) => (
                  <Link
                    key={l}
                    to={switchLocalePath(l)}
                    onClick={() => {
                      localStorage.setItem("preferred_locale", l);
                      setIsMenuOpen(false);
                    }}
                    className="flex items-center gap-2 text-base font-medium py-1 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <img
                      src={LOCALE_FLAGS[l].src}
                      alt={LOCALE_FLAGS[l].label}
                      className="h-4 w-6 object-cover"
                      width={24}
                      height={16}
                    />
                    {LOCALE_FLAGS[l].label}
                  </Link>
                ))}
              </div>
              <Button
                onClick={() => {
                  openCalendly(locale);
                  setIsMenuOpen(false);
                }}
                className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold w-full mt-2 cursor-pointer"
              >
                {t("header.cta")}
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
